import FileDownloadIcon from "@mui/icons-material/FileDownload";
import FilterListIcon from "@mui/icons-material/FilterList";
import PrintIcon from "@mui/icons-material/Print";
import RefreshIcon from "@mui/icons-material/Refresh";
import SearchIcon from "@mui/icons-material/Search";
import { LoadingButton } from "@mui/lab";
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  FormControl,
  GlobalStyles,
  InputLabel,
  MenuItem,
  OutlinedInput,
  Select,
  SelectChangeEvent,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import ListItemText from "@mui/material/ListItemText";
import moment from "moment";
import { useCallback, useMemo, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useRecoilValue } from "recoil";
import { useGetBranches, useGetPaymentMethods } from "src/api/generic";
import { REPORT_BRANCH_MULTISELECT_ALL_SENTINEL } from "src/constants/reportBranchSelect";
import {
  useGetPaymentMovementReportData,
  useGetPaymentTransactionsReport,
  type PaymentMovementReportDataPayload,
  type PaymentMovementTransactionRow,
  type PaymentMovementReportColumn,
} from "src/api/reports";
import DateRangeInput from "src/shared/components/date-range";
import {
  ReportControlBar,
  ReportPageHeading,
  ReportSectionCard,
  reportLayoutTokens,
} from "src/shared/components/report-layout";
import { authState } from "src/store/auth";
import RightArrow from "src/assets/svg-icons/aggregators/right_arrow.svg";
import {
  downloadAsXLS,
  oneBranchHasPermission,
  specificBranchHasPermission,
} from "src/utils";

type FormState = {
  startDate: string;
  endDate: string;
  branchIds: number[];
  paymentMethodId: string;
};

const formatAccounting = (n: number) => {
  const abs = new Intl.NumberFormat(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(Math.abs(n));
  if (n < 0) {
    return `(${abs})`;
  }
  return new Intl.NumberFormat(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(n);
};

const cellFromRow = (
  col: PaymentMovementReportColumn,
  row: PaymentMovementTransactionRow,
  generalYes: string,
  generalNo: string
) => {
  const k = col.key;
  if (k === "reference") return row.reference ?? "";
  if (k === "branch") return row.branch ?? "";
  if (k === "payment_method") return row.payment_method ?? "";
  if (k === "period") return row.period_display ?? row.period ?? "";
  if (k === "payment_qty") {
    const q = row.payment_qty;
    return q === null || q === undefined ? "" : String(q);
  }
  if (k === "net_payment") return formatAccounting(row.net_payment);
  if (k === "linked_to_aggregator")
    return row.linked_to_aggregator ? generalYes : generalNo;
  if (k === "created_by") return row.created_by ?? "";
  if (k === "source") {
    const s = row.source;
    if (s === null || s === undefined) return "";
    return typeof s === "object" ? JSON.stringify(s) : String(s);
  }
  if (k === "balance") return formatAccounting(row.balance);
  return "";
};

const PaymentMovementReportPage = () => {
  const { t } = useTranslation("reports");
  const { t: generalT } = useTranslation("general");
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const auth = useRecoilValue(authState);
  const companyName = auth.user?.company?.name;

  const [filterDraft, setFilterDraft] = useState("");
  const [filterQuery, setFilterQuery] = useState("");

  const formValues = useMemo((): FormState => {
    const b = searchParams.getAll("branches");
    return {
      startDate:
        searchParams.get("start_date") ||
        moment().startOf("month").format("yyyy-MM-DD"),
      endDate:
        searchParams.get("end_date") || moment().endOf("month").format("yyyy-MM-DD"),
      branchIds: b.length ? b.map((x) => Number(x)).filter((n) => Number.isFinite(n)) : [],
      paymentMethodId: searchParams.get("payment_method_id") ?? "",
    };
  }, [searchParams]);

  const { control, setValue } = useForm<FormState>({
    values: formValues,
  });

  const { startDate, endDate, branchIds, paymentMethodId } = formValues;

  const paymentMethodsEnabled = oneBranchHasPermission("download_third_eye_report");

  const { data: branchesData } = useGetBranches();
  const branchesHavePermission =
    branchesData?.data.filter((br) =>
      specificBranchHasPermission("download_third_eye_report", br.id)
    ) ?? [];

  const { data: paymentsMethodsData } = useGetPaymentMethods({
    branches: branchIds,
    enabled: paymentMethodsEnabled,
  });

  const pmIdValid =
    paymentMethodId !== "" && Number.isFinite(Number.parseInt(paymentMethodId, 10));

  const { data: reportBody, isFetching, refetch } = useGetPaymentMovementReportData({
    start_date: startDate,
    end_date: endDate,
    branches: branchIds.length > 0 ? branchIds : undefined,
    payment_method_id: pmIdValid ? paymentMethodId : undefined,
    enabled: true,
  });

  const { refetch: refetchDownload, isFetching: isDownloadLoading } =
    useGetPaymentTransactionsReport({
      start_date: startDate,
      end_date: endDate,
      ...(branchIds.length > 0 ? { branches: branchIds } : {}),
      payment_method_id: paymentMethodId,
      enabled: false,
    });

  const downloadReportsPermission = oneBranchHasPermission("download_third_eye_report");

  const payload = (reportBody as { data?: PaymentMovementReportDataPayload } | null | undefined)
    ?.data;

  const columns = payload?.columns ?? [];
  const transactions = payload?.transactions ?? [];
  const openingBalance = payload?.opening_balance ?? null;

  const applyTableFilter = useCallback(() => {
    setFilterQuery(filterDraft.trim().toLowerCase());
  }, [filterDraft]);

  const filteredTransactions = useMemo(() => {
    const q = filterQuery;
    if (!q) return transactions;
    return transactions.filter((row) => {
      const blob = [
        row.reference,
        row.branch,
        row.payment_method,
        row.created_by,
        typeof row.source === "string" ? row.source : "",
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return blob.includes(q);
    });
  }, [transactions, filterQuery]);

  const periodLabel = useMemo(
    () =>
      t("reportDateRange", {
        start: moment(startDate).format("D MMM YYYY"),
        end: moment(endDate).format("D MMM YYYY"),
      }),
    [t, startDate, endDate]
  );

  const syncToUrl = useCallback(
    (
      next: Partial<{ start: string; end: string; branches: number[]; payment_method_id: string }>
    ) => {
      const p = new URLSearchParams(searchParams);
      if (next.start) p.set("start_date", next.start);
      if (next.end) p.set("end_date", next.end);
      if (next.branches !== undefined) {
        p.delete("branches");
        next.branches.forEach((id) => p.append("branches", String(id)));
      }
      if (next.payment_method_id !== undefined) {
        if (next.payment_method_id !== "") {
          p.set("payment_method_id", next.payment_method_id);
        } else {
          p.delete("payment_method_id");
        }
      }
      setSearchParams(p);
    },
    [searchParams, setSearchParams]
  );

  const handlePrint = () => window.print();

  const handleDownload = useCallback(async () => {
    if (!pmIdValid) return;
    const result = await refetchDownload();
    const d = result.data as string | undefined;
    if (d != null) {
      downloadAsXLS(`\uFEFF${d}`, "payment-movement-report");
    }
  }, [pmIdValid, refetchDownload]);

  const openingColSpan = Math.max(columns.length - 1, 1);
  const tableMinWidth = Math.max(columns.length * 120, 960);

  const yesLabel = generalT("yes");
  const noLabel = generalT("no");

  return (
    <Box p={{ xs: 2, sm: "12px 32px 32px" }} maxWidth="100%">
      <GlobalStyles
        styles={{
          "@media print": {
            "body *": { visibility: "hidden" },
            "#payment-movement-print-root, #payment-movement-print-root *": {
              visibility: "visible",
            },
            "#payment-movement-print-root": {
              position: "absolute",
              left: 0,
              top: 0,
              width: "100%",
            },
          },
        }}
      />

      <Stack
        direction="row"
        alignItems="center"
        gap={1}
        mb={1.5}
        sx={{ "@media print": { display: "none" } }}
      >
        <Typography
          fontSize="0.9rem"
          color="#475467"
          onClick={() => navigate("/reports")}
          sx={{ cursor: "pointer" }}
        >
          {t("thirdEyeReport")}
        </Typography>
        <img src={RightArrow} alt="" style={{ width: 12, height: 12 }} />
        <Box borderRadius="4px" bgcolor="#EAECF0" px={1} py={0.5}>
          <Typography color="#344054" fontWeight={600} fontSize="0.85rem">
            {t("paymentsMovementReport")}
          </Typography>
        </Box>
      </Stack>

      <Box sx={{ "@media print": { display: "none" } }} mb={2}>
        <ReportControlBar
          left={
            <>
              <Controller
                name="branchIds"
                control={control}
                render={({ field }) => (
                  <FormControl size="small" sx={{ minWidth: 220 }}>
                    <InputLabel id="pm-branch" shrink>
                      {generalT("branch")}
                    </InputLabel>
                    <Select<string[]>
                      labelId="pm-branch"
                      multiple
                      value={field.value.map((id) => String(id))}
                      onChange={(e: SelectChangeEvent<string[]>) => {
                        const v = e.target.value;
                        const next = typeof v === "string" ? v.split(",") : v;
                        const strs = next as string[];
                        if (
                          strs.includes(REPORT_BRANCH_MULTISELECT_ALL_SENTINEL)
                        ) {
                          field.onChange([]);
                          syncToUrl({ branches: [] });
                          return;
                        }
                        const ids = strs
                          .map(Number)
                          .filter((n) => n > 0);
                        field.onChange(ids);
                        syncToUrl({ branches: ids });
                      }}
                      input={<OutlinedInput label={generalT("branch")} />}
                      renderValue={(selected) => {
                        if (selected.length === 0) return t("allBranches");
                        return selected
                          .map(
                            (idStr) =>
                              branchesHavePermission.find((b) => String(b.id) === idStr)?.name ??
                              idStr
                          )
                          .join(", ");
                      }}
                    >
                      <MenuItem
                        key="__all-branches-pm"
                        value={REPORT_BRANCH_MULTISELECT_ALL_SENTINEL}
                        sx={{
                          borderBottom: "1px solid #EAECF0",
                          bgcolor: "#F9FAFB",
                        }}
                      >
                        <Typography fontWeight={600} variant="body2" component="span">
                          {t("allBranches")}
                        </Typography>
                      </MenuItem>
                      {branchesHavePermission.map((b) => (
                        <MenuItem key={b.id} value={String(b.id)}>
                          <Checkbox size="small" checked={field.value.includes(b.id)} />
                          <ListItemText primary={b.name} />
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}
              />

              <Controller
                name="paymentMethodId"
                control={control}
                render={({ field }) => (
                  <FormControl size="small" sx={{ minWidth: 200 }}>
                    <InputLabel shrink>{t("paymentMethodBank")}</InputLabel>
                    <Select<string>
                      value={field.value}
                      onChange={(e) => {
                        const v = e.target.value as string;
                        field.onChange(v);
                        syncToUrl({ payment_method_id: v });
                      }}
                      input={<OutlinedInput label={t("paymentMethodBank")} />}
                    >
                      <MenuItem value="">
                        <ListItemText primary={`— ${t("payment")}`} />
                      </MenuItem>
                      {(paymentsMethodsData?.data ?? []).map((method: { id: number | string; name: string }) => (
                        <MenuItem key={method.id} value={String(method.id)}>
                          <ListItemText primary={method.name} />
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}
              />

              <Box sx={{ minWidth: 250 }}>
                <Controller
                  name="startDate"
                  control={control}
                  render={({ field }) => (
                    <DateRangeInput
                      startDate={field.value ? moment(field.value) : null}
                      startDateId="pm-report-start"
                      endDate={formValues.endDate ? moment(formValues.endDate) : null}
                      endDateId="pm-report-end"
                      onDatesChange={(arg: {
                        startDate: moment.Moment | null;
                        endDate: moment.Moment | null;
                      }) => {
                        const s = arg.startDate?.format("yyyy-MM-DD");
                        const e = arg.endDate?.format("yyyy-MM-DD");
                        if (s && e) {
                          field.onChange(s);
                          setValue("endDate", e);
                          syncToUrl({ start: s, end: e });
                        }
                      }}
                    />
                  )}
                />
              </Box>

              <TextField
                size="small"
                placeholder={t("filterAndSearch")}
                value={filterDraft}
                onChange={(e) => setFilterDraft(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    applyTableFilter();
                  }
                }}
                sx={{ minWidth: 200, maxWidth: 260 }}
                InputProps={{
                  startAdornment: (
                    <SearchIcon sx={{ color: "#98A2B3", mr: 0.5 }} fontSize="small" />
                  ),
                }}
              />
              <Button
                variant="outlined"
                color="inherit"
                startIcon={<FilterListIcon fontSize="small" />}
                onClick={applyTableFilter}
                sx={{
                  height: 40,
                  minWidth: 100,
                  borderColor: "#D0D5DD",
                  color: "#344054",
                }}
              >
                {t("search")}
              </Button>
              <Button
                variant="contained"
                color="primary"
                startIcon={<RefreshIcon fontSize="small" />}
                onClick={() => void refetch()}
                disabled={isFetching}
                sx={{ height: 40, minWidth: 120 }}
              >
                {t("reportUpdate")}
              </Button>
            </>
          }
          right={
            <>
              <Button
                variant="outlined"
                color="inherit"
                startIcon={<PrintIcon fontSize="small" />}
                onClick={handlePrint}
                sx={{
                  height: 40,
                  minWidth: 100,
                  borderColor: "#D0D5DD",
                  color: "#344054",
                }}
              >
                {t("print")}
              </Button>
              <LoadingButton
                variant="outlined"
                startIcon={<FileDownloadIcon fontSize="small" />}
                onClick={handleDownload}
                loading={isDownloadLoading}
                disabled={!downloadReportsPermission || !pmIdValid}
                sx={{
                  height: 40,
                  minWidth: 100,
                  borderColor: "#D0D5DD",
                  color: "#344054",
                }}
              >
                {t("export")}
              </LoadingButton>
            </>
          }
        />
      </Box>

      <Box id="payment-movement-print-root">
        <ReportPageHeading
          title={t("paymentsMovementReport")}
          subtitle={companyName ?? payload?.company_name}
          periodLabel={periodLabel}
        />

        <ReportSectionCard>
          {isFetching && payload === undefined ? (
            <Box display="flex" justifyContent="center" py={8}>
              <CircularProgress />
            </Box>
          ) : (
            <TableContainer sx={{ maxWidth: "100%", overflowX: "auto" }}>
              <Table
                size="small"
                stickyHeader
                sx={{ minWidth: tableMinWidth }}
              >
                <TableHead>
                  <TableRow
                    sx={{
                      "& th": {
                        bgcolor: reportLayoutTokens.tableHeaderBg,
                        color: reportLayoutTokens.tableHeaderText,
                        fontWeight: 700,
                        fontSize: "0.69rem",
                        textTransform: "uppercase",
                        borderBottom: reportLayoutTokens.cardBorder,
                        py: 1.25,
                        whiteSpace: "nowrap",
                      },
                    }}
                  >
                    {columns.map((col) => (
                      <TableCell
                        key={col.key}
                        align={
                          ["reference"].includes(col.key)
                            ? "left"
                            : "center"
                        }
                      >
                        {col.label}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {openingBalance !== null &&
                    payload &&
                    columns.length > 0 && (
                      <TableRow
                        sx={{
                          bgcolor: "#F9FAFB",
                          "& td": {
                            fontWeight: 700,
                            fontSize: "0.875rem",
                            borderColor: "#EEF0F2",
                            py: 1,
                          },
                        }}
                      >
                        <TableCell
                          colSpan={openingColSpan}
                          align="left"
                        >
                          {payload?.opening_balance_label ?? ""}
                        </TableCell>
                        <TableCell align="center">
                          {formatAccounting(openingBalance)}
                        </TableCell>
                      </TableRow>
                    )}
                  {filteredTransactions.length === 0 ? (
                    <TableRow>
                      <TableCell
                        colSpan={Math.max(columns.length || 10, 1)}
                        align="center"
                        sx={{ py: 4, color: "text.secondary" }}
                      >
                        {!pmIdValid
                          ? t("paymentMovementSelectMethodHint")
                          : pmIdValid &&
                              filterQuery &&
                              transactions.length > 0
                            ? t("noRowsMatchFilter")
                            : generalT("noData")}
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredTransactions.map((row, ri) => (
                      <TableRow
                        key={`${row.reference ?? "r"}-${ri}`}
                        sx={{
                          "& td": {
                            borderColor: "#EEF0F2",
                            fontSize: "0.875rem",
                            color: "#101828",
                            py: 0.9,
                          },
                        }}
                      >
                        {columns.map((col) => (
                          <TableCell
                            key={col.key}
                            align={
                              col.key === "reference" ? "left" : "center"
                            }
                          >
                            {cellFromRow(col, row, yesLabel, noLabel)}
                          </TableCell>
                        ))}
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </ReportSectionCard>
      </Box>
    </Box>
  );
};

export default PaymentMovementReportPage;
